import { Box, CircularProgress, Step, StepLabel, Stepper } from '@mui/material';
import React, { useState } from 'react';
import { ButtonProps, FormDataConsumer, SimpleForm, Toolbar } from 'react-admin';
import WizardButton from './wizardButton';

interface formStep {
  component: React.ReactElement;
  label?: string;
  onNextFunction?: (formData: any, nextStep: () => void) => void;
  disableToolbarButton?: boolean | null;
  disableToolbar?: boolean | null;
  loadingToolbarButton?: boolean | null;
  customToolbar?: React.ReactElement | null;
}

interface WizardFormProps {
  formSteps: formStep[];
}

const WizardForm: React.FC<WizardFormProps> = ({ formSteps }) => {

  const [index, setIndex] = useState(0);
  const [maxVisitedIndex, setMaxVisitedIndex] = useState(0);

  const nextStep = (value?: number | null) => {
    if (value !== null && value !== undefined) {
      setIndex(value);
      if (value > maxVisitedIndex) {
        setMaxVisitedIndex(value);
      }
      return;
    }

    if ((formSteps.length - 1) > index) {
      const newIndex = index + 1;
      setIndex(newIndex);
      if (newIndex > maxVisitedIndex) {
        setMaxVisitedIndex(newIndex);
      }
    }
  }

  const previousStep = () => {
    if (index > 0) {
      setIndex(index => index - 1);
    }
  }

  const goToStep = (stepIndex: number) => {
    // Só permite navegar para steps já visitados
    if (stepIndex >= 0 && stepIndex <= maxVisitedIndex) {
      setIndex(stepIndex);
    }
  }

  const SimpleFormButton: React.FC<ButtonProps> = ({ ...props }) => (
    <WizardButton
      {...props}
      onClick={(values) => {
        const { onNextFunction } = formSteps[index];
        if (onNextFunction) {
          onNextFunction(values, nextStep);
        } else {
          nextStep();
        }
      }}
    />
  );

  const ToolbarCustom = () => {
    const currentStep = formSteps[index];
    const isLastStep = index === formSteps.length - 1;

    // Se tem toolbar customizado, adiciona botão voltar e retorna
    if (currentStep.customToolbar) {
      return (
        <Toolbar>
          {index > 0 && (
            <WizardButton
              label="Voltar"
              variant="outlined"
              size="medium"
              onClick={previousStep}
              sx={{ marginRight: 2 }}
            />
          )}
          {React.cloneElement(currentStep.customToolbar.props.children as React.ReactElement)}
        </Toolbar>
      );
    }

    // Se está desabilitado, retorna null
    if (currentStep.disableToolbar) {
      return null;
    }

    // Toolbar padrão do wizard
    return (
      <Toolbar>
        {index > 0 && (
          <WizardButton
            label="Voltar"
            variant="outlined"
            size="medium"
            onClick={previousStep}
            sx={{ marginRight: 2 }}
          />
        )}
        {!isLastStep && (
          <SimpleFormButton
            label="Próximo"
            variant="contained"
            size="medium"
            disabled={currentStep.disableToolbarButton || false}
            startIcon={currentStep.loadingToolbarButton && <CircularProgress size={20} />}
          />
        )}
      </Toolbar>
    );
  };

  return (
    <Box style={{ marginTop: 50 }}>
      {formSteps.some(step => step.label) && (
        <Box sx={{ width: '100%', marginBottom: 4, paddingX: 2 }}>
          <Stepper activeStep={index}>
            {formSteps.map((step, idx) => {
              const isClickable = idx <= maxVisitedIndex;
              return (
                <Step
                  key={idx}
                  sx={{ cursor: isClickable ? 'pointer' : 'not-allowed' }}
                  onClick={() => isClickable && goToStep(idx)}
                >
                  <StepLabel
                    sx={{
                      cursor: isClickable ? 'pointer' : 'not-allowed',
                      opacity: isClickable ? 1 : 0.5
                    }}
                  >
                    {step.label || `Step ${idx + 1}`}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
      )}
      <SimpleForm
        toolbar={<ToolbarCustom />}
        warnWhenUnsavedChanges
        sx={{ maxWidth: '1400px' }}
      >
        <FormDataConsumer>
          {({ formData }) =>
            React.cloneElement(formSteps[index].component, { nextStep, SimpleFormButton, formData, previousStep })
          }
        </FormDataConsumer>
      </SimpleForm>
    </Box>
  );
}

export default WizardForm;